import { canViewBasedOnTrack, redacted } from "@lib/utils/anonymization";
import { getHiddenRecommenderMemberIds } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import type { Track } from "@lib/types";
import { isRichTextBlank } from "@lib/isRichTextBlank";
import { richTextToPlainText } from "@lib/isRichTextBlank";
import { wordCount } from "@lib/utils/utils";

// How UI interprets a member's round-level general notes on an ALREADY
// ANONYMIZED aggregate (the only kind the client ever holds). Anonymization
// produces three shapes a naive reader misinterprets:
// - restricted funders' hidden recommenders: notes are DELETED (arrive
//   null/undefined) — absence is stripping, not "hasn't written one";
// - cross-track viewers without the member's feedback-visible-by-default:
//   an EXISTING note is stamped REDACTED, but an absent one stays absent —
//   so any UI keyed on the sentinel alone reveals WHETHER a hidden note
//   exists;
// - everyone else: the string is real content (possibly blank).
// Every UI reading generalNotes goes through these helpers, and the viewer
// matrix in src/tests/generalNotesCardVisibility.test.ts asserts them
// against the real anonymizer's output for each viewer class.

export type GeneralNotesCardState = "hidden" | "placeholder" | "notes";

/**
 * Whether `viewer` is allowed to see `memberId`'s general notes at all —
 * the client-side mirror of the anonymizer's redaction rule (track
 * visibility OR the member's feedback-visible-by-default) composed with the
 * restricted-funder hidden set. `viewerTrack` must be the state context's
 * centrally-derived track (NO_TRACK for non-member SFC admins), never a
 * `round.members` lookup — a non-member admin has no entry.
 */
export function canSeeGeneralNotesOf(
  round: RoundAggregate,
  viewerTrack: Track,
  viewerUserId: string | undefined,
  memberId: string,
): boolean {
  if (getHiddenRecommenderMemberIds(round, viewerUserId ?? "").has(memberId)) {
    return false;
  }

  const member = round.members[memberId];

  if (member == null) {
    return false;
  }

  return (
    canViewBasedOnTrack(viewerTrack, member.track) ||
    member.feedbackVisibleByDefault
  );
}

/**
 * What the funder workspace's General Round Notes card shows for a member:
 * nothing (visibility stripped — must be indistinguishable whether or not a
 * hidden note exists), the empty-state placeholder, or the notes themselves.
 */
export function generalNotesCardState(
  round: RoundAggregate,
  viewerTrack: Track,
  viewerUserId: string | undefined,
  memberId: string,
): GeneralNotesCardState {
  const generalNotes = round.members[memberId]?.generalNotes;

  if (generalNotes === redacted) {
    return "hidden";
  }

  if (!canSeeGeneralNotesOf(round, viewerTrack, viewerUserId, memberId)) {
    return "hidden";
  }

  return isRichTextBlank(generalNotes) ? "placeholder" : "notes";
}

/**
 * The admin activity table's general-notes word count. A track-scoped round
 * admin's aggregate carries the REDACTED sentinel for cross-track notes —
 * counted as 0 (hidden), never as a 1-word note.
 */
export function generalNotesWordCountForActivity(
  generalNotes: string | undefined,
): number {
  if (generalNotes === redacted) {
    return 0;
  }

  return wordCount(richTextToPlainText(generalNotes));
}
